import { useEffect, useState } from "react";
import client from "../lib/api";

function timeAgo(iso) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  const diff = (Date.now() - then) / 1000;
  if (diff < 3600) return `${Math.max(1, Math.floor(diff / 60))}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function SentimentBadge({ sentiment, score }) {
  if (!sentiment || sentiment === "neutral") {
    return (
      <span className="mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ background: "rgba(255,255,255,0.05)", color: "var(--text-tertiary)", border: "1px solid rgba(255,255,255,0.08)" }}>
        neutral
      </span>
    );
  }
  const isPos = sentiment === "positive";
  const bg = isPos ? "rgba(94,234,212,0.14)" : "rgba(243,143,178,0.14)";
  const fg = isPos ? "var(--score-high)" : "var(--pink)";
  const border = isPos ? "rgba(94,234,212,0.24)" : "rgba(243,143,178,0.24)";
  const arrow = isPos ? "▲" : "▼";
  return (
    <span className="mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full inline-flex items-center gap-1" style={{ background: bg, color: fg, border: `1px solid ${border}` }}>
      <span>{arrow}</span>{sentiment} {score != null && `${score >= 0 ? "+" : ""}${score.toFixed(2)}`}
    </span>
  );
}

export default function NewsPanel({ ticker }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    client.get(`/news/${encodeURIComponent(ticker)}`, { params: { days: 7, limit: 5 } })
      .then(r => setItems(r.data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [ticker]);

  return (
    <div className="glass overflow-hidden" data-testid="news-panel">
      <div className="data-header flex items-center justify-between">
        <span>Recent news · 7 days · reputable financial sources</span>
        <span className="mono text-[color:var(--text-tertiary)]">{items.length} article{items.length === 1 ? "" : "s"}</span>
      </div>
      {loading && <div className="data-cell mono text-[color:var(--text-tertiary)] terminal-loader">Fetching news</div>}
      {!loading && items.length === 0 && (
        <div className="data-cell mono text-[color:var(--text-tertiary)]" data-testid="news-empty">
          No qualifying headlines in the last 7 days from reputable sources (Reuters, Bloomberg, FT, WSJ, Barron&apos;s, MarketWatch, CNBC, Business Wire, PR Newswire, AFR).
        </div>
      )}
      <div className="divide-y divide-white/[0.04]">
        {items.map((n) => (
          <a
            key={n.url}
            href={n.url}
            target="_blank"
            rel="noreferrer"
            className="block px-5 py-4 hover:bg-white/[0.03] transition-colors cursor-pointer"
            data-testid={`news-item-${n.url.slice(-16)}`}
          >
            <div className="flex items-center gap-3 mb-1 flex-wrap">
              <span className="mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ background: "rgba(94,234,212,0.10)", color: "var(--cyan)", border: "1px solid rgba(94,234,212,0.20)" }}>{n.publisher}</span>
              <SentimentBadge sentiment={n.sentiment} score={n.sentiment_score} />
              <span className="mono text-[10px] text-[color:var(--text-tertiary)]">{timeAgo(n.published_at)}</span>
              <span className="mono text-[10px] text-[color:var(--text-tertiary)] ml-auto">{(n.published_at || "").slice(0, 10)}</span>
            </div>
            <div className="text-[14px] font-medium text-[color:var(--text-primary)] leading-snug">
              {n.title}
              <span className="ml-2 text-[color:var(--cyan)] text-[12px]">↗</span>
            </div>
            {n.summary && (
              <div className="text-[12px] text-[color:var(--text-secondary)] mt-1 line-clamp-2">{n.summary}</div>
            )}
          </a>
        ))}
      </div>
    </div>
  );
}
